import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { FcmService, Notification } from '../../services/fcm.service';
import { AlertController, LoadingController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  standalone: false,
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  public mobileMenuOpen = false;
  public userMenuOpen = false;
  public consorcioMenuOpen = false;
  public gestionMenuOpen = false;
  public notificationsMenuOpen = false;

  currentUser: any = null;
  selectedConsorcio: any = null;
  notifications: Notification[] = [];
  unreadCount = 0;
  private subs: Subscription = new Subscription();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    public fcmService: FcmService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private titleService: Title
  ) { }

  ngOnInit() {
    // Initialize FCM for push notifications
    this.fcmService.initialize();

    // Subscribe to notifications
    this.subs.add(
      this.fcmService.notifications$.subscribe(notifications => {
        this.notifications = notifications;
      })
    );

    this.subs.add(
      this.fcmService.unreadCount$.subscribe(count => {
        this.unreadCount = count;
      })
    );

    // Subscribe to User and Consortium changes
    this.subs.add(
      this.authService.currentUser.subscribe(user => {
        this.currentUser = user;
      })
    );

    this.subs.add(
      this.authService.selectedConsorcio.subscribe(consorcio => {
        this.selectedConsorcio = consorcio;
        if (consorcio) {
          this.titleService.setTitle(consorcio.nombre);
        } else {
          this.titleService.setTitle('Osaka App');
        }
      })
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onConsorcioChange(event: any) {
    const consorcioId = typeof event === 'object' && event.detail ? event.detail.value : event;
    const selected = this.currentUser?.consorcios.find((c: any) => c.id === consorcioId);
    if (selected) {
      this.authService.setSelectedConsorcio(selected);
      this.titleService.setTitle(selected.nombre);
      this.consorcioMenuOpen = false;
      // Reload current page to refresh data if necessary (optional)
      window.location.reload();
    }
  }

  toggleConsorcioMenu() {
    this.consorcioMenuOpen = !this.consorcioMenuOpen;
    if (this.consorcioMenuOpen) {
      this.userMenuOpen = false;
    }
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  toggleUserMenu() {
    this.userMenuOpen = !this.userMenuOpen;
    if (this.userMenuOpen) {
      this.consorcioMenuOpen = false;
      this.gestionMenuOpen = false;
    }
  }

  toggleGestionMenu() {
    this.gestionMenuOpen = !this.gestionMenuOpen;
    if (this.gestionMenuOpen) {
      this.consorcioMenuOpen = false;
      this.userMenuOpen = false;
      this.notificationsMenuOpen = false;
    }
  }

  toggleNotificationsMenu() {
    this.notificationsMenuOpen = !this.notificationsMenuOpen;
    if (this.notificationsMenuOpen) {
      this.consorcioMenuOpen = false;
      this.userMenuOpen = false;
      this.gestionMenuOpen = false;
    }
  }

  onNotificationClick(notification: Notification) {
    this.fcmService.markAsRead(notification.id);
    this.notificationsMenuOpen = false;

    if (notification.type === 'whatsapp') {
      this.router.navigate(['/whatsapp']);
    } else if (notification.type === 'ticket') {
      this.router.navigate(['/tickets']);
    } else if (notification.data?.link) {
      this.router.navigate([notification.data.link]);
    }
  }

  async logout() {
    const alert = await this.alertController.create({
      header: 'Cerrar Sesión',
      message: '¿Estás seguro que deseas cerrar sesión?',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel'
        },
        {
          text: 'Cerrar Sesión',
          role: 'confirm',
          handler: async () => {
            const loading = await this.loadingController.create({
              message: 'Cerrando sesión...',
              duration: 5000 // Timeout de seguridad
            });
            await loading.present();

            this.authService.logout().subscribe({
              next: async () => {
                await loading.dismiss();
                this.router.navigate(['/login']);
              },
              error: async () => {
                await loading.dismiss();
                this.router.navigate(['/login']);
              }
            });
          }
        }
      ]
    });

    await alert.present();
    this.userMenuOpen = false;
  }

}
